import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-waterfall-chart',
  imports: [DataErrorComponent,CommonModule],
  templateUrl: './waterfall-chart.component.html',
  styleUrl: './waterfall-chart.component.scss'
})
export class WaterfallChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  private filterSub?: Subscription;
  public hasError = false;
  public errorMessage = '';

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {}

  ngAfterViewInit(): void {
    this.loadAndRenderChart();

    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.shouldHandleFilterChange(filters)) {
        return;
      }

      this.loadAndRenderChart(filters);
    });
  }

  private shouldHandleFilterChange(filters?: any): boolean {
    if (!filters || Object.keys(filters).length === 0) {
      return false;
    }

    const triggerWidgetId = String(filters?.widgetId || '').trim();
    if (!triggerWidgetId || triggerWidgetId === this.data?.widgetId) {
      return false;
    }

    return this.getFilterListeners().includes(triggerWidgetId);
  }

  private getFilterListeners(): string[] {
    const configuredListeners = this.data?.filterListener ?? this.data?.filterListner ?? [];

    if (Array.isArray(configuredListeners)) {
      return configuredListeners
        .map((listener) => String(listener || '').trim())
        .filter((listener) => !!listener);
    }

    const singleListener = String(configuredListeners || '').trim();
    return singleListener ? [singleListener] : [];
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    this.getChartRequest(filters).subscribe({
      next: (res) => {
        const chartData = this.normalizeChartData(res);
        const isEmpty = !chartData.category.length || !chartData.series.length;

        if (isEmpty) {
          this.hasError = true;
          this.errorMessage = 'No data found for the selected filters.';
          this.myChart.clear();
        } else {
          this.hasError = false;
          this.renderChart(chartData);
          this.addResizeListener();
        }

        this.myChart.hideLoading();
      },
      error: () => {
        this.hasError = true;
        this.errorMessage = 'No data available for the chart.';
        this.myChart?.clear();
        this.myChart.hideLoading();
      }
    });
  }

  private getChartRequest(filters?: any) {
    const method = this.resolveRequestMethod();

    if (this.data?.payload && !this.isLocalAssetUrl(this.data?.apiUrl)) {
      return this.configService.getApi(
        this.data.apiUrl,
        {
          queryParams: filters?.queryParams || {},
          rawQueryString: filters?.rawQueryString
        },
        {
          skipDefaultPayload: true,
          customPayload: this.buildRequestPayload(filters),
          useQueryParam: this.data?.useQueryParams === true
        },
        method
      );
    }

    return this.configService.getConfig(
      this.data.apiUrl,
      {
        ...(filters || {}),
        useQueryParams: this.data?.useQueryParams || false
      },
      method
    );
  }

  private resolveRequestMethod(): 'post' | 'get' {
    const configuredMethod = String(this.data?.method || '').trim().toLowerCase();
    if (configuredMethod === 'get' || configuredMethod === 'post') {
      return configuredMethod;
    }

    return this.isLocalAssetUrl(this.data?.apiUrl) ? 'get' : 'post';
  }

  private isLocalAssetUrl(url: string): boolean {
    const normalizedUrl = String(url || '').trim().toLowerCase();
    return normalizedUrl.startsWith('assets/') || normalizedUrl.endsWith('.json');
  }

  private buildRequestPayload(filters?: any): any {
    const basePayload = this.deepClone(this.data?.payload || {});
    const requestParams = filters?.requestParams || {};

    if (Object.keys(requestParams).length === 0) {
      return basePayload;
    }

    if (
      basePayload?.requestParams &&
      typeof basePayload.requestParams === 'object' &&
      !Array.isArray(basePayload.requestParams)
    ) {
      return {
        ...basePayload,
        requestParams: {
          ...basePayload.requestParams,
          ...requestParams
        }
      };
    }

    return {
      ...basePayload,
      ...requestParams
    };
  }

  private deepClone<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private normalizeChartData(response: any): { category: string[]; series: any[] } {
    const source = this.resolveChartDataSource(response);
    const configuredCategory = this.resolveConfiguredPath(response, this.data?.xAxis);
    const configuredSeries = this.resolveConfiguredPath(response, this.data?.series);

    const category = Array.isArray(configuredCategory)
      ? configuredCategory
      : Array.isArray(source?.category)
        ? source.category
        : [];

    const series = Array.isArray(configuredSeries)
      ? configuredSeries
      : Array.isArray(source?.series)
        ? source.series
        : [];

    return { category, series };
  }

  private resolveChartDataSource(response: any): any {
    const dataKey = String(this.data?.dataKey || '').trim();

    if (dataKey) {
      const resolved = this.resolvePath(response, dataKey);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    if (response?.data && typeof response.data === 'object') {
      return response.data;
    }

    return response;
  }

  private resolveConfiguredPath(response: any, path: string): any {
    const normalizedPath = String(path || '').trim();
    if (!normalizedPath) {
      return undefined;
    }

    return this.resolvePath(response, normalizedPath);
  }

  private resolvePath(obj: any, path: string): any {
    try {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    } catch {
      return undefined;
    }
  }


  private renderChart(chartData?: { category: string[]; series: any[] }): void {

    const option = {
      title: {
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: function (params: any) {
          let tar;
          if (params[1] && params[1].value !== '-') {
            tar = params[1];
          } else {
            tar = params[2];
          }
          return `${tar.name}<br/>${tar.seriesName} : ${tar.value}`;
        }
      },
      legend: {
        data: (chartData?.series || [])
          .map((seriesItem: any) => seriesItem?.name)
          .filter((name: string) => !!name)
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: chartData?.category || []
      },
      yAxis: {
        type: 'value'
      },
      series: chartData?.series
    };

    this.myChart.setOption(option);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);
  }

  private addResizeListener(): void {
    window.removeEventListener('resize', this.handleResize);
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };


  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
